import {graphql} from '@apollo/client/react/hoc';
import getStepType from 'core/libs/getStepType';
import decodeId from 'core/libs/helpers/decodeId';
import redirectIfNotSubscribed from 'core/libs/hoc/redirectIfNotSubscribed';
import spinnerWhileLoading from 'core/libs/hoc/spinnerWhileLoading';
import log from 'core/libs/logger';
import PropTypes from 'prop-types';
import React from 'react';
import {injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import {
  branch,
  compose,
  renderComponent,
  setPropTypes,
  withProps,
} from 'recompose';
import NotFoundPage from 'theme/pages/NotFound';
import ShouldRegisterModal from '../components/ShouldRegisterModal';
import Step from '../components/Step';
import SIDEBAR_COURSE from '../graphql/sidebarCourse.graphql';
import withStep from '../hoc/withStep';

const DisplayShouldRegisterModal = () => <ShouldRegisterModal />;

const enhance = compose(
  injectIntl,
  redirectIfNotSubscribed,
  connect(
    ({auth, theme: {codechallengeSidebarVisible, sidebar}, user}) => ({
      authenticated: auth.authenticated,
      sidebar: (codechallengeSidebarVisible && sidebar) || false,
      user: user.profile,
    }),
    (dispatch) => ({
      closeSidebar: () => dispatch({type: 'CLOSE_SIDEBAR'}),
    }),
  ),
  setPropTypes({
    courseSlug: PropTypes.string.isRequired,
    stepSlug: PropTypes.string.isRequired,
    trackSlug: PropTypes.string.isRequired,
    authenticated: PropTypes.bool,
  }),
  spinnerWhileLoading(() => typeof window === 'undefined'),
  withStep,
  graphql(SIDEBAR_COURSE, {
    alias: 'withSidebarData',
    options: ({courseSlug}) => ({
      variables: {
        courseSlug,
      },
    }),
    props: ({data, ownProps: {courseSlug, trackSlug}}) => {
      const {courses, error, loading} = data;
      if (loading) return {sidebarLoading: loading};
      if (error) return {error};

      if (courses && courses.edges && !courses.edges[0]) {
        log("Didn't find a course for this slug", {
          courseSlug,
          currentUrl: typeof window !== 'undefined' && window.location.href,
        });
        return {error: "Didn't find a course for this slug"};
      }

      const course =
        courses && courses.edges && courses.edges[0] && courses.edges[0].node;
      let additionalData = {};

      const tracks =
        courses &&
        courses.edges &&
        courses.edges[0] &&
        courses.edges[0].node &&
        courses.edges[0].node.trackstepSet &&
        courses.edges[0].node.trackstepSet.edges;

      let track =
        tracks &&
        tracks.find(
          ({
            node: {
              track: {slug},
            },
          }) => slug === trackSlug,
        );

      if (track) {
        track = track.node && track.node.track; // eslint-disable-line prefer-destructuring
      } else {
        track = tracks && tracks[0] && tracks[0].node && tracks[0].node.track; // eslint-disable-line prefer-destructuring
      }

      if (track) {
        additionalData = {
          trackstepSet: null,
          track: {
            ...track,
            id: decodeId(track.id),
          },
        };
      }
      return {
        sidebarLoading: false,
        course: {
          ...course,
          id: decodeId(course.id),
          stageSet: undefined,
          stages:
            course &&
            course.stageSet &&
            course.stageSet.edges &&
            course.stageSet.edges.map(({node: stage}) => {
              return {
                ...stage,
                id: decodeId(stage.id),
                stepSet: undefined,
                steps:
                  stage &&
                  stage.stepSet &&
                  stage.stepSet.edges &&
                  stage.stepSet.edges.map(({node: step}) => {
                    return {
                      ...step,
                      ctype: getStepType(step),
                      id: decodeId(step.id),
                    };
                  }),
              };
            }),
        },
        ...additionalData,
      };
    },
  }),
  spinnerWhileLoading(({loading, sidebarLoading}) => loading || sidebarLoading),
  branch(
    ({error}) => error,
    () => NotFoundPage,
  ),
  branch(
    ({authenticated}) => !authenticated,
    renderComponent(DisplayShouldRegisterModal),
  ),
  withProps(
    ({
      course,
      step: {
        stage: {id},
      },
    }) => {
      const relatedStage =
        (course &&
          course.stages &&
          course.stages.find((stage) => stage.id === id)) ||
        {};
      return {
        relatedSteps: relatedStage.steps,
      };
    },
  ),
);

export default enhance(Step);
